import { Component, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { Observable, Subject, of } from 'rxjs';
import { first, switchMap, take, takeUntil } from 'rxjs/operators';
import _ from 'lodash';

import { Account } from '../../models/account';
import { Region } from '../../models/content';
import { AccountService } from '../../services/account.service';
import { ModalService } from '../../services/modal.service';
import { PicalunaSiteService } from '../../services/picaluna-site.service';

import { ContentRegionComponent } from './content-region.component';

import { CmsPage } from '../../models/content';
import { contentSlug } from '../../content-slug';

/**
 * Content module — Regions list. Mirrors the funeral-costs-list CRUD loop, but
 * persists via accountService.patchContent (the RBAC-gated /account/content
 * route), and uses HARD delete (the route replaces the whole collection, so a
 * removed item is gone; use the `published` toggle to hide without deleting).
 */
@Component({
  selector: 'app-content-region-list',
  templateUrl: './content-region-list.component.html',
})
export class ContentRegionListComponent implements OnInit, OnDestroy {
  /** True until ensureFullAccount() resolves. account$ replays a cached,
   *  content-STRIPPED account instantly, so gating on the observable alone
   *  renders an empty table instead of the loading state. */
  accountLoading = true;

  private unsubscribe$ = new Subject<void>();
  account$: Observable<Account>;

  /** Slug currently publishing, so only that row spins. */
  publishingSlug: string | null = null;

  constructor(
    private modalService: ModalService,
    private accountService: AccountService,
    private dialogService: DialogService,
    private picalunaSiteService: PicalunaSiteService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    this.account$ = this.accountService.account$;
  }

  ngOnInit(): void {
    // The default account fetch omits the content subtree (payload diet);
    // load it on demand so this editor has data to show.
    this.accountService.ensureFullAccount().subscribe({
      next: () => (this.accountLoading = false),
      error: () => (this.accountLoading = false),
    });
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  regionsAsList(content: any): Region[] {
    const data = content?.regions;
    if (!data) return [];
    const list = Array.isArray(data) ? data.filter(Boolean) : Object.values(data).filter(Boolean);
    return _.sortBy(list as Region[], (r) => r?._meta?.index ?? Number.MAX_SAFE_INTEGER);
  }

  onAddRegion(): void {
    const account = this.snapshot();
    if (!account) return;
    const existing = this.regionsAsList(account.data?.content);
    const seed: Region = {
      _meta: { id: `id-region-${this.shortId()}`, index: this.nextIndex(existing) },
      slug: '',
      name: '',
      state: 'NSW',
      published: true,
    };
    this.openEditor(seed, true);
  }

  /**
   * Publish just this region — and its suburbs — to the website.
   *
   * Suburbs have no publish action of their own: picaluna-v3 keeps them in a
   * separate file that is merged per region, so publishing "Sydney" refreshes
   * Sydney's suburbs and carries every other region's forward untouched. That
   * is also why a suburb edit sets `suburbsChangedAt` on its region — the badge
   * in this list is the only thing that would tell you the edit is still
   * sitting unpublished.
   *
   * Bidirectional, like the blog action: a region with Published off is REMOVED
   * from the site by this, because the scope means "make the site match
   * Conductor for this item".
   */
  onPublishRegion(region: Region): void {
    const slug = (region?.slug || '').trim();
    if (!slug || this.publishingSlug) {
      return;
    }

    const isLive = region.published !== false;
    const name = region.name || slug;

    const modal = this.modalService.generic({
      title: isLive ? 'Publish this region?' : 'Remove this region from the site?',
      copy: isLive
        ? [
            `This publishes "${name}" and its suburbs to the website preview.`,
            'No other region is touched — their suburbs stay exactly as they are on the site now.',
          ]
        : [
            `"${name}" is hidden, so publishing it will REMOVE it and its suburb pages from the website.`,
            'Turn Published on first if you meant to put it live.',
          ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: 'p-button-text' },
        {
          label: isLive ? 'Publish this region' : 'Remove from site',
          key: 'publish',
          class: isLive ? 'p-button-success' : 'p-button-danger',
        },
      ],
    });

    modal?.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (key: string) => {
        if (key !== 'publish') {
          return;
        }

        this.publishingSlug = slug;

        this.picalunaSiteService
          // Data + layout publish TOGETHER: when this region has a designed
          // page, its token rides along — a region publish must never ship
          // half of what the person sees as "the Sydney page".
          .publish(
            (() => {
              let scope = this.publishScope('regions', region);
              const page = this.designedPage(region);
              if (page) scope += ` pages:${this.pagePublishSlug(page.path)}`;
              // Suburbs ship with their region — their designed pages ride too.
              for (const sp of this.suburbDesignedPages(region)) {
                scope += ` pages:${this.pagePublishSlug(sp.path)}`;
              }
              return scope;
            })()
          )
          // take(1), not takeUntil: the publish DISPATCH must outlive the
          // dialog — takeUntil aborted it mid-flight and skipped the
          // publishedSlug breadcrumb, so the NEXT rename lost its redirect.
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.publishingSlug = null;
              // Records what shipped (so a later rename takes the old page
              // down) and clears the badge, in a single write.
              this.recordPublishAndClearBadge(region._meta.id, slug);
              this.modalService.generic({
                title: 'Publishing started',
                copy: [
                  `"${name}" and its suburbs are being published. It takes a few minutes.`,
                  'Track progress on the Website page, then publish to production from there when the preview looks right.',
                ],
                buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
              });
            },
            error: (err) => {
              this.publishingSlug = null;
              this.modalService.error(err?.error?.error || err);
            },
          });
      },
    });
  }

  /**
   * Record what shipped and clear the unpublished-suburb-changes badge, in ONE
   * write.
   *
   * Dispatched, not completed — the workflow takes minutes and we do not poll
   * here. Erring towards clearing early means the badge tracks "did you press
   * publish", which is the question it exists to answer. A failed run shows up
   * on the Website page, which is where pipeline state belongs.
   *
   * Single write on purpose: patchContentItem sends the whole region and the
   * server's upsert is delete-then-rewrite with no version check, so each call
   * is a chance to revert a field another content manager changed.
   */
  /**
   * Record what shipped and clear the unpublished-suburb-changes badge, in ONE
   * write against a FRESHLY re-read region.
   *
   * patchContentItem sends the whole record and the server's upsert is
   * delete-then-rewrite with no version check, so writing the copy this dialog
   * has held since it opened would revert anything another content manager
   * changed meanwhile. Re-reading immediately before the write shrinks that
   * window to the request itself, and doing both mutations in one write halves
   * the number of chances to collide.
   *
   * The badge clears on DISPATCH, not completion — it answers "did you press
   * publish". Run outcomes live on the Website page, which already polls them.
   */
  private recordPublishAndClearBadge(metaId: string, publishedSlug: string): void {
    // NOT gated on takeUntil(unsubscribe$). This is a fire-and-forget
    // breadcrumb that must outlive the dialog: closing the list during the
    // fetch cancelled the whole chain, so the slug was never recorded and the
    // next rename could not take the old page down. first() completes both
    // observables on their own, so nothing leaks.
    this.accountService
      .get(true)
      .pipe(first())
      .subscribe({
        next: () => {
          const account = this.snapshot();
          const rows = account?.data?.content?.regions;
          if (!Array.isArray(rows)) return;
          const fresh = (rows as Region[]).find((r) => r?._meta?.id === metaId);
          if (!fresh) return;
          if (!fresh.suburbsChangedAt && fresh.publishedSlug === publishedSlug) return;

          delete fresh.suburbsChangedAt;
          fresh.publishedSlug = publishedSlug;
          this.accountService
            .patchContentItem('regions', fresh)
            .pipe(first())
            .subscribe({ error: () => {} });
        },
        error: () => {},
      });
  }

  onEditRegion(region: Region): void {
    this.openEditor(_.cloneDeep(region), false);
  }

  onDeleteRegion(region: Region): void {
    const modal = this.modalService.generic({
      title: 'Remove region',
      copy: [
        `Remove "${region.name || region._meta.id}"? Its suburb pages will stay on picaluna.com until the NEXT publish — run one when ready. To hide it without deleting, uncheck "Published" instead.`,
      ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: '' },
        { label: 'Remove', key: 'confirm', class: 'p-button-danger', icon: 'pi pi-trash' },
      ],
    });
    modal.onClose.subscribe({
      next: (reason: string) => {
        if (reason?.toString().toLowerCase() !== 'confirm') return;
        const account = this.snapshot();
        if (!account?.data?.content?.regions || !Array.isArray(account.data.content.regions)) return;
        account.data.content.regions = (account.data.content.regions as Region[]).filter(
          (r) => r?._meta?.id !== region._meta.id
        );
        this.persistRemove(region._meta.id);
      },
    });
  }

  hubPath(region: Region): string {
    return `/${(region.state || 'nsw').toLowerCase()}/${region.slug}`;
  }

  /** The region's designed page (the CMS record at its hub address), if any. */
  designedPage(region: Region): CmsPage | undefined {
    if (!region.slug) return undefined;
    const content: any = this.snapshot()?.data?.content ?? {};
    const rawPages: any = content.pages;
    const pages: CmsPage[] = (Array.isArray(rawPages) ? rawPages : Object.values(rawPages || {})).filter(
      (p: any) => p && typeof p === 'object'
    );
    const canon = (v: string) => (String(v || '').trim().replace(/\/+$/, '') || '/');
    const hub = this.hubPath(region);
    return pages.find((p) => canon(p.path) === hub);
  }

  /**
   * Designed SUBURB pages of this region (Suburb-editor pages at
   * /{state}/{region}/funeral-services/{suburb}). Suburbs ship with their
   * region, so the region's publish must carry these page records too — a
   * region publish that shipped the suburbs but not their designed pages
   * would flip those pages back to the automatic layout.
   */
  private suburbDesignedPages(region: Region): CmsPage[] {
    if (!region.slug) return [];
    const content: any = this.snapshot()?.data?.content ?? {};
    const rawSuburbs: any = content.suburbs;
    const suburbSlugs = new Set(
      (Array.isArray(rawSuburbs) ? rawSuburbs : Object.values(rawSuburbs || {}))
        .filter((sb: any) => sb && sb.regionId === region._meta.id && typeof sb.slug === 'string')
        .map((sb: any) => sb.slug)
    );
    if (!suburbSlugs.size) return [];
    const rawPages: any = content.pages;
    const pages: CmsPage[] = (Array.isArray(rawPages) ? rawPages : Object.values(rawPages || {})).filter(
      (p: any) => p && typeof p === 'object'
    );
    const canon = (v: string) => (String(v || '').trim().replace(/\/+$/, '') || '/');
    const prefix = `${this.hubPath(region)}/`;
    return pages.filter((p) => {
      const segs = canon(p.path).split('/').filter(Boolean);
      return canon(p.path).startsWith(prefix) && segs.length === 4 && suburbSlugs.has(segs[3]);
    });
  }

  /** picaluna-v3's publish id for a page path ("/nsw/sydney" → "nsw-sydney"). */
  private pagePublishSlug(path: string): string {
    const trimmed = (path || '').replace(/^\/+|\/+$/g, '');
    return trimmed === '' ? 'home' : contentSlug(trimmed.replace(/\//g, '-'));
  }

  private openEditor(region: Region, isNew: boolean): void {
    // The editor mutates this same object via Object.assign, so the pre-edit
    // identity must be captured NOW — comparing region to result.region
    // after close compares an object to itself.
    const ref = this.dialogService.open(ContentRegionComponent, {
      // Esc would close the editor and silently discard unsaved changes.
      closeOnEscape: false,
      header: isNew ? 'Add region' : `Edit ${region.name || 'region'}`,
      width: '90vw',
      baseZIndex: 11000,
      data: {
        region,
        isNew,
        designedPage: region.slug ? this.designedPage(region) : undefined,
        // Editor-performed save: region write first, page write on its
        // completion (the deadlock serialization), dialog open until BOTH
        // land — so Publish can't race the pair.
        save: (result: { region: Region; pageAction?: any }) => {
          const pa = result.pageAction;
          const pageWrite =
            pa?.action === 'upsert' && pa.page
              ? this.accountService.patchContentItem('pages', pa.page).pipe(take(1))
              : pa?.action === 'remove' && pa.id
                ? this.accountService.removeContentItem('pages', pa.id).pipe(take(1))
                : of(null);
          return this.accountService
            .patchContentItem('regions', result.region)
            .pipe(take(1), switchMap(() => pageWrite));
        },
      },
    });
    ref.onClose.subscribe({
      next: (result: { saved: boolean; region: Region; pageAction?: any; persisted?: boolean } | undefined) => {
        if (!result?.saved) return;
        // The writes happened INSIDE the editor (save callback above) —
        // here we only reconcile the local list state.
        const account = this.snapshot();
        if (!account) return;
        if (!account.data.content) account.data.content = {};
        if (!Array.isArray(account.data.content.regions)) account.data.content.regions = [];
        const arr = account.data.content.regions as Region[];
        const i = arr.findIndex((r) => r?._meta?.id === result.region._meta.id);
        if (i >= 0) arr[i] = result.region;
        else arr.push(result.region);
        if (!result.persisted) this.persistItem(result.region);
      },
    });
  }

  /**
   * Writes are take(1) with NO takeUntil.
   *
   * takeUntil(unsubscribe$) on a write ABORTS the in-flight request when the
   * modal closes, so a save or delete could be silently cancelled by closing
   * the dialog that started it — and it completed the stream empty, so first()
   * threw EmptyError ("no elements in sequence"). A content write must outlive
   * its dialog, like recordPublishedSlug below. Errors are surfaced rather than
   * swallowed: a rejected save that leaves the row on screen reads as success.
   */
  // Per-item persistence: sends ONLY the edited/removed record, so two
  // content managers working in different regions can't clobber each other.
  /** andThen runs only after the region write lands — see onClose's ordering note. */
  private persistItem(item: any, andThen?: () => void): void {
    this.accountService
      .patchContentItem('regions', item)
      .pipe(take(1))
      .subscribe({
        next: () => andThen?.(),
        error: (err) => this.modalService.error(err?.error?.error || err),
      });
  }

  private persistRemove(id: string): void {
    this.accountService
      .removeContentItem('regions', id)
      .pipe(take(1))
      .subscribe({ error: (err) => this.modalService.error(err?.error?.error || err) });
  }

  private snapshot(): Account | undefined {
    let s: Account | undefined;
    this.accountService.account$.pipe(first()).subscribe({ next: (a) => (s = a) });
    return s;
  }
  private nextIndex(existing: Region[]): number {
    return existing.reduce((acc, r) => {
      const i = r?._meta?.index;
      return typeof i === 'number' && i > acc ? i : acc;
    }, -10) + 10;
  }
  private shortId(): string {
    return Math.random().toString(36).slice(2, 10);
  }

  /**
   * The scope for a per-item publish, taking the OLD page down when the slug
   * changed since it was last published.
   *
   * The scoped merge keys on slug, so a renamed item's previous record is not in
   * scope and gets carried forward untouched while the new one is appended —
   * leaving both URLs live. Sending the old token too resolves it to a takedown,
   * because an id in scope that Conductor no longer has is removed.
   */
  private publishScope(type: string, item: { slug?: string; publishedSlug?: string }): string {
    const slug = (item.slug || '').trim();
    const prev = (item.publishedSlug || '').trim();
    return prev && prev !== slug ? `${type}:${prev} ${type}:${slug}` : `${type}:${slug}`;
  }

}
